import type { AxiosInstance } from 'axios';

// Core functions: perform raw HTTP only
const listOwnerProductsCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
  /** Filter and sort parameters, starting with "&" (`buildInventoryListQuery`). */
  query = '',
) => {
  return axiosinstance.get(
    `/bp_get_all_owner_products_information?page=${page}&limit=${limit}${query}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getStockValueInformationCore = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
  /** Filter parameters, starting with "?" (`buildStockValueQuery`); empty for the whole store. */
  query = '',
) => {
  return axiosinstance.get(`/bp_get_stock_value_information${query}`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

const getProductCategoriesInformationCore = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(`/bp_get_product_categories_information`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

const createOwnerProductsCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  payload: any,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.post(
    `/bp_add_product_information`,
    JSON.stringify(payload),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const addProductAttributeCore = async (
  axiosinstance: AxiosInstance,
  payload: {
    bp_attribute_name: string;
    bp_attribute_values: string[];
  },
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.post(
    `/bp_add_product_attribute`,
    JSON.stringify(payload),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getProductAttributesCore = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(`/bp_get_product_attributes`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

const deleteProductAttributeCore = async (
  axiosinstance: AxiosInstance,
  attributeId: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.delete(
    `/bp_delete_product_attribute/${encodeURIComponent(attributeId)}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const searchOwnerProductsCore = async (
  axiosinstance: AxiosInstance,
  searchText: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
  /** Filter and sort parameters, starting with "&" (`buildInventoryListQuery`). */
  query = '',
) => {
  return axiosinstance.get(
    `/bp_owner_search_all_products_information?bp_search_text=${encodeURIComponent(
      searchText,
    )}&page=${page}&limit=${limit}${query}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const updateOwnerProductCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  payload: any,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.patch(
    `/bp_update_product_information`,
    JSON.stringify(payload),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// NOTE: createOwnerProductsCore is defined above

const getProductByIdCore = async (
  axiosinstance: AxiosInstance,
  productId: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(`/bp_get_product_information/${productId}`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

const deleteOwnerProductCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  productId: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.post(
    `/bp_delete_product_information/${productId}`,
    JSON.stringify({}),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Record product wastage
const updateProductWastageCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  payload: {
    bp_product_id: string;
    bp_product_quantity: string; // amount wasted
    bp_variant_id?: string;
    bp_wastage_reason?: string;
    bp_wastage_employee_id?: string;
  },
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.patch(
    `/bp_update_wasted_product_information`,
    JSON.stringify(payload),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

/** Body of `PATCH /bp_adjust_product_quantity`: stock added (or removed) as a delta. */
export type AdjustProductQuantityPayload = {
  bp_product_id: string;
  /** Amount to add; the server applies it with `$inc`, never as a new total. */
  bp_quantity_delta: number;
  bp_variant_id?: string;
};

// Add received stock without sending the whole product
const adjustProductQuantityCore = async (
  axiosinstance: AxiosInstance,
  payload: AdjustProductQuantityPayload,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.patch('/bp_adjust_product_quantity', payload, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

// Speculative based on backoffice report patterns; if backend differs, adjust path
const getProductSalesTrendCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  productId: string,
  startDateISO: string,
  endDateISO: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_single_product_sales_report_information?bp_product_id=${encodeURIComponent(
      productId,
    )}&bp_start_date=${encodeURIComponent(startDateISO)}&bp_end_date=${encodeURIComponent(
      endDateISO,
    )}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// New: Monthly product sales report (matches web GraphContext)
const getProductMonthlySalesCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  year: number | string,
  productId: string,
  auth: { accessToken?: string | null },
  variantId?: string,
) => {
  const variantQuery = variantId
    ? `&bp_variant_id=${encodeURIComponent(variantId)}`
    : '';
  return axiosinstance.get(
    `/bp_get_product_monthly_sales_report_information?bp_year=${encodeURIComponent(
      String(year),
    )}&bp_product_id=${encodeURIComponent(productId)}${variantQuery}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Daily Opening and Closing Stocks (speculative path; align with backend)
const getDailyOpeningClosingStocksCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  dateISO: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_daily_opening_and_closing_stocks_report_information?page=${page}&limit=${limit}&bp_date=${encodeURIComponent(
      dateISO,
    )}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Updated Products Report by date range (kept for backwards compatibility)
const getUpdatedProductsReportCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startDateISO: string,
  endDateISO: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_updated_products_report_information?page=${page}&limit=${limit}&bp_start_date=${encodeURIComponent(
      startDateISO,
    )}&bp_end_date=${encodeURIComponent(endDateISO)}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Updated Products Report by month and year (matches web implementation)
const getUpdatedProductsReportByMonthYearCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  month: number | string,
  year: number | string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_updated_products_reports_information?page=${page}&limit=${limit}&bp_month=${encodeURIComponent(
      String(month),
    )}&bp_year=${encodeURIComponent(String(year))}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Transfer product between locations
const transferProductBetweenLocationsCore = async (
  axiosinstance: AxiosInstance,
  payload: {
    productId: string;
    variantId?: string;
    fromLocationId: string;
    toLocationId: string;
    quantity: number;
  },
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.post('/bp_transfer_product_between_locations', payload, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

// Public wrappers
export const listOwnerProducts = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
  query = '',
) => {
  try {
    return await listOwnerProductsCore(
      axiosinstance,
      ownerId,
      page,
      limit,
      auth,
      query,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

// NOTE: exported below (single definition)

export const searchOwnerProducts = async (
  axiosinstance: AxiosInstance,
  searchText: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
  query = '',
) => {
  try {
    return await searchOwnerProductsCore(
      axiosinstance,
      searchText,
      page,
      limit,
      auth,
      query,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getStockValueInformation = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
  query = '',
) => {
  try {
    return await getStockValueInformationCore(axiosinstance, auth, query);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getProductCategoriesInformation = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getProductCategoriesInformationCore(axiosinstance, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateOwnerProduct = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  payload: any,
  auth: { accessToken?: string | null },
) => {
  try {
    return await updateOwnerProductCore(axiosinstance, ownerId, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const createOwnerProducts = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  payload: any,
  auth: { accessToken?: string | null },
) => {
  try {
    return await createOwnerProductsCore(axiosinstance, ownerId, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const addProductAttribute = async (
  axiosinstance: AxiosInstance,
  payload: {
    bp_attribute_name: string;
    bp_attribute_values: string[];
  },
  auth: { accessToken?: string | null },
) => {
  try {
    return await addProductAttributeCore(axiosinstance, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getProductAttributes = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getProductAttributesCore(axiosinstance, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const deleteProductAttribute = async (
  axiosinstance: AxiosInstance,
  attributeId: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await deleteProductAttributeCore(axiosinstance, attributeId, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const deleteOwnerProduct = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  productId: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await deleteOwnerProductCore(
      axiosinstance,
      ownerId,
      productId,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateProductWastage = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  payload: {
    bp_product_id: string;
    bp_product_quantity: string; // amount wasted
    bp_variant_id?: string;
    bp_wastage_reason?: string;
    bp_wastage_employee_id?: string;
  },
  auth: { accessToken?: string | null },
) => {
  try {
    return await updateProductWastageCore(
      axiosinstance,
      ownerId,
      payload,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * Adds stock to a product or one of its variants at the current location.
 * Needs the owner's token: the server looks the account up from it.
 */
export const adjustProductQuantity = async (
  axiosinstance: AxiosInstance,
  payload: AdjustProductQuantityPayload,
  auth: { accessToken?: string | null },
) => {
  try {
    return await adjustProductQuantityCore(axiosinstance, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const transferProductBetweenLocations = async (
  axiosinstance: AxiosInstance,
  payload: {
    productId: string;
    variantId?: string;
    fromLocationId: string;
    toLocationId: string;
    quantity: number;
  },
  auth: { accessToken?: string | null },
) => {
  try {
    return await transferProductBetweenLocationsCore(
      axiosinstance,
      payload,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getProductSalesTrend = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  productId: string,
  startDateISO: string,
  endDateISO: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getProductSalesTrendCore(
      axiosinstance,
      ownerId,
      productId,
      startDateISO,
      endDateISO,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getProductMonthlySales = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  year: number | string,
  productId: string,
  auth: { accessToken?: string | null },
  /** Limits the report to one variant (servers that support it echo `bp_variant_id`). */
  variantId?: string,
) => {
  try {
    return await getProductMonthlySalesCore(
      axiosinstance,
      ownerId,
      year,
      productId,
      auth,
      variantId,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getDailyOpeningClosingStocks = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  dateISO: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getDailyOpeningClosingStocksCore(
      axiosinstance,
      ownerId,
      page,
      limit,
      dateISO,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getUpdatedProductsReport = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startDateISO: string,
  endDateISO: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getUpdatedProductsReportCore(
      axiosinstance,
      ownerId,
      page,
      limit,
      startDateISO,
      endDateISO,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getUpdatedProductsReportByMonthYear = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  month: number | string,
  year: number | string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getUpdatedProductsReportByMonthYearCore(
      axiosinstance,
      ownerId,
      page,
      limit,
      month,
      year,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getProductById = async (
  axiosinstance: AxiosInstance,
  productId: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getProductByIdCore(axiosinstance, productId, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};
